/**
 * Cliente REST de las REGLAS DE APROBACIÓN (M1). Endpoints server-authoritative
 * fuera de sync (mismo patrón que `assessment/api.ts`): progreso del alumno,
 * matriz del roster, config efectiva, y los write-paths validados+gateados.
 */
import { apiJson } from "@/lib/rest";
import type { CompletionConfig } from "@space/shared";

export interface CriterionBreakdownDTO {
  key: string;
  met: boolean;
  current: number;
  target: number;
  detail: string;
  params: Record<string, unknown>;
}

export interface CompletionStatusDTO {
  passed: boolean;
  configured: boolean;
  criteria: CriterionBreakdownDTO[];
}

export interface CompletionReportRow {
  user_id: string;
  name: string | null;
  email: string | null;
  final_display: string;
  passed: boolean;
  criteria: CriterionBreakdownDTO[];
}

export interface CompletionReportDTO {
  cohort_id: string;
  configured: boolean;
  rows: CompletionReportRow[];
}

export interface CompletionConfigDTO {
  config: CompletionConfig;
  configured: boolean;
}

const enc = encodeURIComponent;

/** Progreso de completion del PROPIO alumno en una cohorte. */
export async function fetchMyCompletion(cohortId: string): Promise<CompletionStatusDTO> {
  return apiJson(`/api/cohorts/${enc(cohortId)}/my-completion`);
}

/** Matriz de completion del roster (manager). */
export async function fetchCompletionReport(cohortId: string): Promise<CompletionReportDTO> {
  return apiJson(`/api/cohorts/${enc(cohortId)}/completion-report`);
}

/** Config efectiva de una cohorte (para cargar el editor). */
export async function fetchCohortCompletionConfig(cohortId: string): Promise<CompletionConfigDTO> {
  return apiJson(`/api/cohorts/${enc(cohortId)}/completion-config`);
}

/** Fija (config) o limpia (null) las reglas de una cohorte. */
export async function saveCohortCompletionConfig(
  cohortId: string,
  config: CompletionConfig | null,
): Promise<{ ok: boolean; configured: boolean }> {
  return apiJson(`/api/cohorts/${enc(cohortId)}/completion-config`, {
    method: "PUT",
    body: JSON.stringify({ config }),
  });
}

/** Fija (config) o limpia (null) las reglas de un curso (plantilla). */
export async function saveCourseCompletionConfig(
  courseId: string,
  config: CompletionConfig | null,
): Promise<{ ok: boolean; configured: boolean }> {
  return apiJson(`/api/courses/${enc(courseId)}/completion-config`, {
    method: "PUT",
    body: JSON.stringify({ config }),
  });
}

/** Marca/desmarca manualmente a un alumno como completo. */
export async function markCompletion(
  cohortId: string,
  userId: string,
  complete: boolean,
): Promise<{ ok: boolean }> {
  return apiJson(`/api/cohorts/${enc(cohortId)}/completion/mark`, {
    method: "POST",
    body: JSON.stringify({ user_id: userId, complete }),
  });
}
